import { useParams, useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import { cn, formatDate, jsonToQueryString } from '../lib/utils';
import Pill from '../components/ui/Pill';
import Icon, { IconName } from '../components/ui/Icon';
import { Button } from '../components/ui/Button';
import { useMutation } from '@tanstack/react-query';
import { getCompany } from '@/api/companies';
import { getCampaign } from '@/api/campaign';
import { getContact } from '@/api/contacts';
import Avatar from '../components/ui/Avatar';
import { getUser } from '@/api/user';
import { getTargetList } from '@/api/target-list';
import type { WebsiteUrlRule } from '@/types/websiteUrlRule';
import { sanitizeWebsiteUrlRules } from '@/app/lib/websiteUrlRules';

type FilterUiMap = {
  label: string;
  type?: string;
  keys?: string[];
  icon?: IconName;
  className?: string;
  options?: {
    id: string;
    value: string;
    meta?: Record<string, unknown>;
  }[];
  onClear?: () => void;
  multiple?: boolean;
  disableFutureDates?: boolean;
  disablePastDates?: boolean;
};

const useFilter = (
  availableFilters: string[],
  filterUiMap?: Record<string, FilterUiMap>,
  onClearAll?: () => void
) => {
  const [additionalData, setAdditionalData] = useState<Record<string, any>>({});
  // get it from url
  const searchParams = useSearchParams();
  const params = useParams();
  const clientId = params.clientId as string;
  const {
    mutate: getCompanyMutation,
    data: missingCompany,
    isPending: isLoadingMissingCompany,
    isError: isErrorMissingCompany,
  } = useMutation({
    mutationFn: (id: string) => getCompany(clientId, id),
  });

  const {
    mutate: getTargetListMutation,
    data: missingTargetList,
    isPending: isLoadingMissingTargetList,
    isError: isErrorMissingTargetList,
  } = useMutation({
    mutationFn: (id: string) => getTargetList(clientId, id),
  });

  const {
    mutate: getCampaignMutation,
    data: missingCampaign,
    isPending: isLoadingMissingCampaign,
    isError: isErrorMissingCampaign,
  } = useMutation({
    mutationFn: (id: string) => getCampaign(clientId, id),
  });

  const {
    mutate: getContactMutation,
    data: missingContact,
    isPending: isLoadingMissingContact,
    isError: isErrorMissingContact,
  } = useMutation({
    mutationFn: (id: string) => getContact(clientId, id),
  });

  const {
    mutate: getUserMutation,
    data: missingUser,
    isPending: isLoadingMissingUser,
    isError: isErrorMissingUser,
  } = useMutation({
    mutationFn: (id: string) => getUser(clientId, id),
  });

  const filter = useMemo(() => {
    const newFilter: Record<string, string | object | string[]> = {};

    searchParams.forEach((value, key) => {
      if (availableFilters.includes(key)) {
        const filterConfig = filterUiMap?.[key];
        if (filterConfig?.type === 'website-url-rules' || filterConfig?.type === 'query-builder') {
          try {
            const rules = JSON.parse(value) as unknown[];
            if (Array.isArray(rules) && rules.length > 0) {
              newFilter[key] = rules;
            }
          } catch {
            // Ignore malformed URL state.
          }
          return;
        } else if (filterConfig?.multiple && !newFilter[key]) {
          newFilter[key] = [];
        }
        if (filterConfig?.multiple && Array.isArray(newFilter[key])) {
          newFilter[key].push(value);
        } else {
          newFilter[key] = value;
        }
      }
    });

    availableFilters.forEach(filterKey => {
      const filterConfig = filterUiMap?.[filterKey];
      if (
        filterConfig?.type === 'daterange' ||
        filterConfig?.type === 'range-slider' ||
        filterConfig?.type === 'number-range' ||
        filterConfig?.type === 'range-select'
      ) {
        const startValue = searchParams.get(filterConfig.keys?.[0] || 'startDate');
        const endValue = searchParams.get(filterConfig.keys?.[1] || 'endDate');
        if (startValue || endValue) {
          if (filterConfig?.type === 'range-slider') {
            newFilter[filterKey] = [Number(startValue), Number(endValue)];
          } else if (
            filterConfig?.type === 'number-range' ||
            filterConfig?.type === 'range-select'
          ) {
            newFilter[filterKey] = {
              min: startValue || '',
              max: endValue || '',
            };
          } else {
            newFilter[filterKey] = {
              startDate: startValue,
              endDate: endValue,
            };
          }
        }
      }
    });

    return newFilter;
  }, [searchParams.toString(), availableFilters, filterUiMap]);

  const handleFilterChange = (key: string, value: string | object | boolean) => {
    if (availableFilters.includes(key)) {
      const url = new URL(window.location.href);
      const filterConfig = filterUiMap?.[key];
      // Clear range-slider/daterange by removing both keys
      if (
        (filterConfig?.type === 'website-url-rules' || filterConfig?.type === 'query-builder') &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        url.searchParams.set(
          key,
          JSON.stringify(
            filterConfig?.type === 'website-url-rules'
              ? sanitizeWebsiteUrlRules(value as WebsiteUrlRule[])
              : value
          )
        );
      } else if (
        filterConfig?.type === 'website-url-rules' ||
        filterConfig?.type === 'query-builder'
      ) {
        url.searchParams.delete(key);
      } else if (
        (filterConfig?.type === 'daterange' ||
          filterConfig?.type === 'range-slider' ||
          filterConfig?.type === 'number-range' ||
          filterConfig?.type === 'range-select') &&
        (!value || value === '')
      ) {
        url.searchParams.delete(filterConfig.keys?.[0] || 'startDate');
        url.searchParams.delete(filterConfig.keys?.[1] || 'endDate');
      } else if (
        (filterConfig?.type === 'daterange' ||
          filterConfig?.type === 'range-slider' ||
          filterConfig?.type === 'number-range' ||
          filterConfig?.type === 'range-select') &&
        typeof value === 'object' &&
        value
      ) {
        if (filterConfig?.type === 'range-slider' && Array.isArray(value)) {
          const [min, max] = value;
          if (min === 0 && max === 100) {
            url.searchParams.delete(filterConfig.keys?.[0] || 'startDate');
            url.searchParams.delete(filterConfig.keys?.[1] || 'endDate');
          } else {
            url.searchParams.set(filterConfig.keys?.[0] || 'startDate', String(min));
            url.searchParams.set(filterConfig.keys?.[1] || 'endDate', String(max));
          }
        } else if (filterConfig?.type === 'number-range' || filterConfig?.type === 'range-select') {
          const range = value as { min?: string; max?: string };
          if (range.min) {
            url.searchParams.set(filterConfig.keys?.[0] || 'min', range.min);
          } else {
            url.searchParams.delete(filterConfig.keys?.[0] || 'min');
          }
          if (range.max) {
            url.searchParams.set(filterConfig.keys?.[1] || 'max', range.max);
          } else {
            url.searchParams.delete(filterConfig.keys?.[1] || 'max');
          }
        } else {
          const dateRange = value as { startDate: string; endDate: string };
          if (dateRange.startDate && dateRange.endDate) {
            url.searchParams.set(filterConfig.keys?.[0] || 'startDate', dateRange.startDate);
            url.searchParams.set(filterConfig.keys?.[1] || 'endDate', dateRange.endDate);
          } else {
            url.searchParams.delete(filterConfig.keys?.[0] || 'startDate');
            url.searchParams.delete(filterConfig.keys?.[1] || 'endDate');
          }
        }
      } else {
        if (value && typeof value === 'string') {
          url.searchParams.set(key, value);
        } else if (value && typeof value === 'object' && Array.isArray(value)) {
          url.searchParams.delete(key);
          if (value.length > 0) {
            const uniqueValues = new Set(value);
            uniqueValues.forEach((item: string) => {
              url.searchParams.append(key, item);
            });
          }
        } else {
          url.searchParams.delete(key);
          if (filterConfig?.type === 'daterange') {
            url.searchParams.delete(filterConfig.keys?.[0] || 'startDate');
            url.searchParams.delete(filterConfig.keys?.[1] || 'endDate');
          }
        }
      }

      window.history.pushState({}, '', url.toString());
    }
  };

  const filterSearchParams = useMemo(() => {
    const apiParams: Record<string, string> = {};
    const arrayParams: Record<string, string[]> = {};

    Object.entries(filter).forEach(([key, value]) => {
      const filterConfig = filterUiMap?.[key];

      if (filterConfig?.type === 'range-slider' && Array.isArray(value)) {
        const [min, max] = value;
        if (min !== 0 || max !== 100) {
          apiParams[filterConfig.keys?.[0] || 'startDate'] = String(min);
          apiParams[filterConfig.keys?.[1] || 'endDate'] = String(max);
        }
      } else if (
        (filterConfig?.type === 'number-range' || filterConfig?.type === 'range-select') &&
        typeof value === 'object' &&
        value
      ) {
        const range = value as { min?: string; max?: string };
        if (range.min) {
          apiParams[filterConfig.keys?.[0] || 'min'] = range.min;
        }
        if (range.max) {
          apiParams[filterConfig.keys?.[1] || 'max'] = range.max;
        }
      } else if (filterConfig?.type === 'daterange' && typeof value === 'object' && value) {
        const dateRange = value as { startDate: string; endDate: string };
        if (dateRange.startDate && dateRange.endDate) {
          apiParams[filterConfig.keys?.[0] || 'startDate'] = dateRange.startDate;
          apiParams[filterConfig.keys?.[1] || 'endDate'] = dateRange.endDate;
        }
      } else if (filterConfig?.type === 'website-url-rules' && Array.isArray(value)) {
        apiParams[key] = JSON.stringify(sanitizeWebsiteUrlRules(value as WebsiteUrlRule[]));
      } else if (filterConfig?.type === 'query-builder' && Array.isArray(value)) {
        apiParams[key] = JSON.stringify(value);
      } else if (typeof value === 'string' && value) {
        apiParams[key] = value;
      } else if (typeof value === 'object' && Array.isArray(value)) {
        if (arrayParams[key]) {
          arrayParams[key].push(...value);
        } else {
          arrayParams[key] = value;
        }
      }
    });

    const url = new URL(window.location.origin + '?' + jsonToQueryString(apiParams));
    const params = new URLSearchParams(url.searchParams);
    Object.entries(arrayParams).forEach(([key, value]) => {
      value.forEach((item: string) => {
        params.append(key, item);
      });
    });
    return params;
  }, [filter, filterUiMap]);

  const clearFilters = () => {
    // update it in url
    const url = new URL(window.location.href);
    availableFilters.forEach(key => {
      const filterConfig = filterUiMap?.[key];
      if (
        filterConfig?.type === 'daterange' ||
        filterConfig?.type === 'range-slider' ||
        filterConfig?.type === 'number-range' ||
        filterConfig?.type === 'range-select'
      ) {
        url.searchParams.delete(filterConfig.keys?.[0] || 'startDate');
        url.searchParams.delete(filterConfig.keys?.[1] || 'endDate');
      } else if (
        filterConfig?.type === 'website-url-rules' ||
        filterConfig?.type === 'query-builder'
      ) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.delete(key);
      }
    });
    window.history.pushState({}, '', url.toString());
    onClearAll?.();
  };

  const selectedFilterUi = useMemo(() => {
    if (Object.keys(filter).length === 0) return null;
    const totalFilters = Object.keys(filter).length;
    return (
      <div className="flex items-start gap-2 justify-between w-full">
        <div className="flex items-center flex-wrap gap-2">
          {Object.entries(filter).map(([key, value], index) => {
            const filterUi = filterUiMap?.[key];
            let option;

            if (Array.isArray(value) && filterUi?.type === 'range-slider') {
              option = {
                id: `${value[0]}_${value[1]}`,
                value: `${value[0]} - ${value[1]}`,
              };
            } else if (
              (filterUi?.type === 'number-range' || filterUi?.type === 'range-select') &&
              typeof value === 'object' &&
              value
            ) {
              const range = value as { min?: string; max?: string };
              const rangeOption = filterUi?.options?.find((option: any) => {
                const optionRange = option.meta?.range;
                return (
                  String(optionRange?.min || '') === String(range.min || '') &&
                  String(optionRange?.max || '') === String(range.max || '')
                );
              });
              option = {
                id: `${range.min || ''}_${range.max || ''}`,
                value:
                  rangeOption?.value ||
                  (range.min && range.max
                    ? `${range.min} - ${range.max}`
                    : range.min
                      ? `Min ${range.min}`
                      : `Max ${range.max}`),
              };
            } else if (
              typeof value === 'object' &&
              value &&
              'startDate' in value &&
              'endDate' in value
            ) {
              const dateRange = value as { startDate: string; endDate: string };
              option = {
                id: `${dateRange.startDate}_${dateRange.endDate} `,
                value: `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)} `,
              };
            } else if (filterUi?.type === 'website-url-rules' && Array.isArray(value)) {
              option = {
                id: key,
                value: `${value.length} Selected`,
              };
            } else {
              if (filterUi?.type === 'target-list-select') {
                option = filterUi?.options?.find(option => option.id === value);

                if (!option) {
                  if (missingTargetList && missingTargetList.id === value) {
                    option = {
                      id: missingTargetList.id,
                      value: missingTargetList.name || '-',
                    };
                  } else {
                    // temporary option
                    option = additionalData[key] || {
                      id: value as string,
                      value: value as string,
                    };
                    if (
                      !filterUi?.multiple &&
                      !isLoadingMissingTargetList &&
                      !isErrorMissingTargetList
                    ) {
                      getTargetListMutation(value as string);
                    }
                  }
                }
              } else if (filterUi?.type === 'company-select') {
                option = filterUi?.options?.find(option => option.id === value);

                if (!option) {
                  if (missingCompany && missingCompany.id === value) {
                    option = {
                      id: missingCompany.id,
                      value: missingCompany.name.full || missingCompany.name.short || '-',
                    };
                  } else {
                    option = additionalData[key] || {
                      id: value as string,
                      value: value as string,
                    };
                    if (!filterUi?.multiple && !isLoadingMissingCompany && !isErrorMissingCompany) {
                      getCompanyMutation(value as string);
                    }
                  }
                }
              } else if (filterUi?.type === 'campaign-select') {
                option = filterUi?.options?.find(option => option.id === value);
                if (!option) {
                  if (missingCampaign && missingCampaign.id === value) {
                    option = {
                      id: missingCampaign.id,
                      value: missingCampaign?.name || '-',
                    };
                  } else {
                    // temporary option
                    option = additionalData[key] || {
                      id: value as string,
                      value: value as string,
                    };
                    if (
                      !filterUi?.multiple &&
                      !isLoadingMissingCampaign &&
                      !isErrorMissingCampaign
                    ) {
                      // call api to get the campaign
                      getCampaignMutation(value as string);
                    }
                  }
                }
              } else if (filterUi?.type === 'contact-select') {
                option = filterUi?.options?.find(option => option.id === value);
                if (!option) {
                  if (missingContact && missingContact.id === value) {
                    option = {
                      id: missingContact.id,
                      value:
                        (missingContact.contact_info?.name?.first || '') +
                          ' ' +
                          (missingContact.contact_info?.name?.last || '') || '-',
                      prefix: (
                        <div className="mr-2">
                          <Avatar
                            size={32}
                            name={
                              (missingContact.contact_info?.name?.first || '') +
                              ' ' +
                              (missingContact.contact_info?.name?.last || '')
                            }
                            src={missingContact.contact_info?.photo_url}
                          />
                        </div>
                      ),
                    };
                  } else {
                    // temporary option
                    option = additionalData[key] || {
                      id: value as string,
                      value: value as string,
                    };
                    if (!filterUi?.multiple && !isLoadingMissingContact) {
                      // call api to get the contact
                      getContactMutation(value as string);
                    }
                  }
                }
              } else if (filterUi?.type === 'user-select') {
                option = filterUi?.options?.find(option => option.id === value);
                if (!option) {
                  if (missingUser && missingUser.id === value) {
                    option = {
                      id: missingUser.id,
                      value: missingUser.first_name + ' ' + missingUser.last_name || '-',
                      prefix: (
                        <div className="mr-2">
                          <Avatar
                            size={32}
                            name={missingUser.first_name + ' ' + missingUser.last_name}
                            src={missingUser.profile_pic_url}
                          />
                        </div>
                      ),
                    };
                  } else {
                    // temporary option
                    option = additionalData[key] || {
                      id: value as string,
                      value: value as string,
                    };
                    if (!filterUi?.multiple && !isLoadingMissingUser && !isErrorMissingUser) {
                      // call api to get the user
                      getUserMutation(value as string);
                    }
                  }
                }
              } else {
                option = filterUi?.options?.find(option => option.id === value) ||
                  additionalData[key] || {
                    id: value as string,
                    value: value as string,
                  };
              }
            }

            return (
              <div
                key={key}
                className={cn(
                  index === 0 && 'pl-0',
                  index !== totalFilters - 1 && 'border-r pr-2 border-border'
                )}
              >
                <Pill
                  color="neutral"
                  className="py-1 pl-1 pr-0.5 h-auto text-xs capitalize"
                  rounded={false}
                >
                  <span className="text-muted-foreground font-medium leading-4 inline-flex items-center">
                    {filterUi?.icon && (
                      <Icon name={filterUi.icon} size={4} className="mr-1 text-muted-foreground" />
                    )}
                    <span className="text-muted-foreground font-medium leading-4">
                      {filterUi?.label || key} :
                    </span>
                  </span>

                  <span
                    className={cn(
                      'text-muted-foreground font-medium leading-4',
                      filterUi?.className
                    )}
                  >
                    {filterUi?.multiple && Array.isArray(value)
                      ? `${value.length} Selected`
                      : option.value}
                  </span>
                  <Icon
                    name="cross-fill"
                    size={4}
                    className="ml-1 cursor-pointer hover:text-foreground"
                    onClick={() => {
                      handleFilterChange(key, '');
                      filterUi?.onClear?.();
                    }}
                  />
                </Pill>
              </div>
            );
          })}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 text-error-base relative h-auto mt-[2px]"
          onClick={clearFilters}
        >
          Clear all
        </Button>
      </div>
    );
  }, [
    filter,
    filterUiMap,
    missingCompany,
    isLoadingMissingCompany,
    missingCampaign,
    isLoadingMissingCampaign,
    missingContact,
    isLoadingMissingContact,
    missingUser,
    isLoadingMissingUser,
    isErrorMissingUser,
    isErrorMissingCompany,
    isErrorMissingCampaign,
    isErrorMissingContact,
  ]);

  const filterCount = useMemo(() => {
    return Object.keys(filter).length;
  }, [filter]);

  return {
    filter,
    setFilter: handleFilterChange,
    filterSearchParams,
    clearFilters,
    selectedFilterUi,
    filterCount,
    additionalData,
    setAdditionalData,
  };
};

export default useFilter;
